import React, { createContext, useContext, useState, ReactNode } from "react";
import { Category } from "@lib/shared/types";

interface Question {
    [key: string]: any;
}

interface ListingState {
    questions: { [key: string]: any };
    loading: boolean;
}

interface SellingBoxState {
    insuranceCategory: Category | null;
}

interface InsuranceState {
    insuranceCategory: string;
    questions: Question[];
    loading: boolean;
    listing: ListingState;
    sellingBox: SellingBoxState;
}

interface InsuranceContextType {
    insuranceState: InsuranceState;
    setInsuranceCategory: (category: Category | null) => void;
    setQuestions: (questions: Question[]) => void;
    setLoading: (loading: boolean) => void;
    setListingLoading: (loading: boolean) => void;
    initiateQuestionnaireQuestions: (questions: { [key: string]: any }) => void;
    setQuestionValue: (payload: { name: string; value: string }) => void;
}

const InsuranceContext = createContext<InsuranceContextType | undefined>(
    undefined,
);

export const InsuranceProvider = ({ children }: { children: ReactNode }) => {
    const [insuranceState, setInsuranceState] = useState<InsuranceState>({
        insuranceCategory: "",
        questions: [],
        loading: false,
        listing: {
            questions: {},
            loading: false,
        },
        sellingBox: {
            insuranceCategory: null,
        },
    });

    const setInsuranceCategory = (category: Category | null) => {
        setInsuranceState((prev) => ({
            ...prev,
            sellingBox: { ...prev.sellingBox, insuranceCategory: category },
        }));
    };

    const setQuestions = (questions: Question[]) => {
        setInsuranceState((prev) => ({ ...prev, questions }));
    };

    const setLoading = (loading: boolean) => {
        setInsuranceState((prev) => ({ ...prev, loading }));
    };

    const setListingLoading = (loading: boolean) => {
        setInsuranceState((prev) => ({
            ...prev,
            listing: { ...prev.listing, loading },
        }));
    };

    const initiateQuestionnaireQuestions = (questions: {
        [key: string]: any;
    }) => {
        setInsuranceState((prev) => ({
            ...prev,
            listing: { ...prev.listing, questions },
        }));
    };

    const setQuestionValue = (payload: { name: string; value: string }) => {
        setInsuranceState((prev) => ({
            ...prev,
            listing: {
                ...prev.listing,
                questions: {
                    ...prev.listing.questions,
                    [payload.name]: {
                        ...((prev.listing.questions[payload.name] as
                            | Record<string, any>
                            | undefined) ?? {}),
                        value: payload.value,
                    },
                },
            },
        }));
    };

    return (
        <InsuranceContext.Provider
            value={{
                insuranceState,
                setInsuranceCategory,
                setQuestions,
                setLoading,
                setListingLoading,
                initiateQuestionnaireQuestions,
                setQuestionValue,
            }}
        >
            {children}
        </InsuranceContext.Provider>
    );
};

export const useInsurance = () => {
    const context = useContext(InsuranceContext);
    if (context === undefined) {
        throw new Error(
            "useInsurance must be used within an InsuranceProvider",
        );
    }
    return context;
};
